import React, { useState, useRef, useEffect, useCallback, useMemo } from 'react';
import EmojiPicker from 'emoji-picker-react';
import {
  Send, Clock, AlertTriangle, MessageSquare, Paperclip,
  Smile, Tag, Monitor, User,
  Zap, X, ChevronDown, ChevronUp, MoreVertical,
  Bold, Italic, Underline, Strikethrough, List, ListOrdered, Palette, Eraser, Lock, LogOut
} from 'lucide-react';
import ReactMarkdown from 'react-markdown';
import api from '../utils/api';
import DeleteConfirmModal from '../components/DeleteConfirmModal';
import CustomDropdown from '../components/CustomDropdown';
import { useToast } from '../context/ToastContext';

// ===== CUSTOMER INTELLIGENCE PANEL =====
const CustomerIntelligencePanel = ({ session, messages = [], tags = [] }) => {
  const [expandedSection, setExpandedSection] = useState('profile');
  const [contactData, setContactData] = useState(null);
  const [isLoadingContact, setIsLoadingContact] = useState(false);
  const [registeredParams, setRegisteredParams] = useState([]);

  useEffect(() => {
    const fetchWidgetConfig = async () => {
      try {
        const config = await api.getWidgetConfig();
        if (config && config.custom_parameters) {
          setRegisteredParams(config.custom_parameters);
        }
      } catch (e) {
        console.error("Failed to fetch widget config for parameters", e);
      }
    };
    fetchWidgetConfig();
  }, []);

  useEffect(() => {
    const fetchContactInfo = async () => {
      if (!session) return;
      setIsLoadingContact(true);
      try {
        const searchVal = session.user_email || session.user_id || session.user_phone || session.session_id;
        if (searchVal) {
          const res = await api.getContacts(1, 5, searchVal);
          if (res && res.contacts && res.contacts.length > 0) {
            setContactData(res.contacts[0]);
          } else {
            setContactData(null);
          }
        } else {
          setContactData(null);
        }
      } catch (e) {
        console.error("Failed to fetch contact details", e);
      } finally {
        setIsLoadingContact(false);
      }
    };
    fetchContactInfo();
  }, [session]);

  if (!session) {
    return (
      <div style={{
        width: '280px',
        background: '#ffffff',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        color: '#94a3b8',
        fontSize: '13px',
        borderRadius: '16px',
        border: '1px solid rgba(148,163,184,0.15)',
        boxShadow: '0 4px 6px -1px rgba(0,0,0,0.05), 0 2px 4px -1px rgba(0,0,0,0.03)',
        overflow: 'hidden'
      }}>
        Select a chat to view customer details
      </div>
    );
  }



  const parseDate = (dateVal) => {
    if (!dateVal) return null;
    let timestamp = dateVal;
    if (typeof dateVal === 'string') {
      if (/^\d+$/.test(dateVal)) {
        timestamp = parseInt(dateVal, 10);
      } else {
        const d = new Date(dateVal);
        return isNaN(d.getTime()) ? null : d;
      }
    }
    if (typeof timestamp === 'number') {
      if (timestamp < 10000000000) {
        return new Date(timestamp * 1000);
      }
      return new Date(timestamp);
    }
    const d = new Date(dateVal);
    return isNaN(d.getTime()) ? null : d;
  };

  const formatDate = (dateString) => {
    const date = parseDate(dateString);
    if (!date) return 'N/A';
    return date.toLocaleDateString('en-IN', { month: 'short', day: 'numeric', year: 'numeric' });
  };

  const formatTime = (dateString) => {
    const date = parseDate(dateString);
    if (!date) return 'N/A';
    return date.toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit', hour12: true });
  };

  const SectionHeader = ({ icon: Icon, title, sectionId }) => (
    <div
      onClick={() => setExpandedSection(expandedSection === sectionId ? null : sectionId)}
      style={{
        padding: '12px 16px',
        background: expandedSection === sectionId ? '#f0f7ff' : '#f8fafc',
        borderBottom: '1px solid rgba(0,0,0,0.05)',
        cursor: 'pointer',
        display: 'flex',
        alignItems: 'center',
        gap: '10px',
        transition: 'background 0.2s'
      }}
      onMouseOver={(e) => e.currentTarget.style.background = '#f1f5f9'}
      onMouseOut={(e) => e.currentTarget.style.background = expandedSection === sectionId ? '#f0f7ff' : '#f8fafc'}
    >
      <Icon size={14} color="#2563eb" />
      <span style={{ fontSize: '11px', fontWeight: '700', color: '#111827', textTransform: 'uppercase', letterSpacing: '0.05em', flex: 1 }}>
        {title}
      </span>
      {expandedSection === sectionId ? <ChevronUp size={14} color="#64748b" /> : <ChevronDown size={14} color="#64748b" />}
    </div>
  );

  const assignedHumanAgent = session.assigned_to_name || session.taken_over_by_name || 'None';

  return (
    <div style={{
      width: '280px',
      background: '#ffffff',
      display: 'flex',
      flexDirection: 'column',
      overflow: 'hidden',
      borderRadius: '12px',
      border: '1px solid #e4e4e7',
      boxShadow: '0 1px 2px rgba(0,0,0,0.04)'
    }}>
      {/* Header */}
      <div style={{
        padding: '16px',
        borderBottom: '1px solid rgba(0,0,0,0.05)',
        background: 'linear-gradient(135deg, #83bbe1 0%, #5da9d9 100%)'
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
          <div style={{
            width: '40px',
            height: '40px',
            borderRadius: '10px',
            border: '2px solid rgba(255,255,255,0.4)',
            overflow: 'hidden',
            flexShrink: 0
          }}>
            <img
              src={`https://api.dicebear.com/7.x/micah/svg?seed=${session.user_id || session.session_id}&backgroundColor=b6e3f4,c0aede,d1d4f9`}
              alt="User Avatar"
              style={{ width: '100%', height: '100%', objectFit: 'cover' }}
            />
          </div>
          <div style={{ minWidth: 0, flex: 1 }}>
            <div style={{ color: '#ffffff', fontSize: '13px', fontWeight: '700', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
              {session.user_name || session.user_email || session.user_phone || `Visitor ${session.session_id ? session.session_id.slice(-6).toUpperCase() : ''}`}
            </div>
            <div style={{ color: 'rgba(255,255,255,0.85)', fontSize: '11px', marginTop: '2px', fontFamily: 'monospace', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
              ID: {session.session_id}
            </div>
          </div>
        </div>
      </div>


      <div style={{ padding: '12px 16px', borderBottom: '1px solid rgba(0,0,0,0.05)', background: '#ffffff' }}>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '8px' }}>
          <div style={{ padding: '10px', borderRadius: '12px', background: '#f8fafc', border: '1px solid #e2e8f0' }}>
            <div style={{ fontSize: '9px', fontWeight: '800', color: '#64748b', textTransform: 'uppercase', marginBottom: '3px' }}>Group</div>
            <div style={{ fontSize: '12px', fontWeight: '700', color: '#111827' }}>{session.group || session.group_name || 'Default Group'}</div>
          </div>
          <div style={{ padding: '10px', borderRadius: '12px', background: '#f8fafc', border: '1px solid #e2e8f0' }}>
            <div style={{ fontSize: '9px', fontWeight: '800', color: '#64748b', textTransform: 'uppercase', marginBottom: '3px' }}>Priority</div>
            <div style={{ fontSize: '12px', fontWeight: '700', color: '#111827' }}>{session.priority || 'Normal'}</div>
          </div>
          <div style={{ padding: '10px', borderRadius: '12px', background: '#f8fafc', border: '1px solid #e2e8f0', gridColumn: 'span 2' }}>
            <div style={{ fontSize: '9px', fontWeight: '800', color: '#64748b', textTransform: 'uppercase', marginBottom: '3px' }}>Assigned Human Agent</div>
            <div style={{ fontSize: '12px', fontWeight: '700', color: '#111827' }}>{assignedHumanAgent}</div>
          </div>
        </div>
      </div>

      {/* Content */}
      <div style={{ flex: 1, overflowY: 'auto', paddingBottom: '16px' }} className="custom-scrollbar">

        {/* Identity Attributes */}
        <div>
          <SectionHeader icon={User} title="Identity Attributes" sectionId="profile" />
          {expandedSection === 'profile' && (
            <div style={{ padding: '16px', borderBottom: '1px solid rgba(0,0,0,0.05)' }}>
              {isLoadingContact ? (
                <div style={{ padding: '10px', textAlign: 'center', color: '#94a3b8', fontSize: '11px' }}>
                  Loading...
                </div>
              ) : (
                <>
                  <div style={{ marginBottom: '12px' }}>
                    <div style={{ fontSize: '10px', fontWeight: '700', color: '#64748b', textTransform: 'uppercase', marginBottom: '4px' }}>Name</div>
                    <div style={{ fontSize: '13px', color: '#111827', fontWeight: '500' }}>
                      {(contactData?.name || session.user_name || 'N/A').replace(/\s*\([a-f0-9-]{36}\)/i, '')}
                    </div>
                  </div>
                  <div style={{ marginBottom: '12px' }}>
                    <div style={{ fontSize: '10px', fontWeight: '700', color: '#64748b', textTransform: 'uppercase', marginBottom: '4px' }}>Session ID</div>
                    <div style={{ fontSize: '12px', color: '#111827', fontWeight: '500', fontFamily: 'monospace', wordBreak: 'break-all' }}>
                      {session.session_id || 'N/A'}
                    </div>
                  </div>
                  <div style={{ marginBottom: '12px' }}>
                    <div style={{ fontSize: '10px', fontWeight: '700', color: '#64748b', textTransform: 'uppercase', marginBottom: '4px' }}>Customer ID</div>
                    <div style={{ fontSize: '12px', color: '#111827', fontWeight: '500', fontFamily: 'monospace', wordBreak: 'break-all' }}>
                      {contactData?.userid || session.user_id || 'N/A'}
                    </div>
                  </div>
                  {((contactData?.email && contactData.email !== 'N/A') || session.user_email) && (
                    <div style={{ marginBottom: '12px' }}>
                       <div style={{ fontSize: '10px', fontWeight: '700', color: '#64748b', textTransform: 'uppercase', marginBottom: '4px' }}>Email</div>
                       <div style={{ fontSize: '13px', color: '#2563eb', fontWeight: '500' }}>{(contactData?.email && contactData.email !== 'N/A') ? contactData.email : session.user_email}</div>
                    </div>
                  )}
                  {((contactData?.phone && contactData.phone !== 'N/A') || session.user_phone) && (
                    <div>
                       <div style={{ fontSize: '10px', fontWeight: '700', color: '#64748b', textTransform: 'uppercase', marginBottom: '4px' }}>Phone</div>
                       <div style={{ fontSize: '13px', color: '#111827', fontWeight: '500' }}>{(contactData?.phone && contactData.phone !== 'N/A') ? contactData.phone : session.user_phone}</div>
                    </div>
                  )}
                </>
              )}
            </div>
          )}
        </div>

        {/* Custom Parameters */}
        <div>
          <SectionHeader icon={Tag} title="Custom Parameters" sectionId="custom_params" />
          {expandedSection === 'custom_params' && (
            <div style={{ padding: '16px', borderBottom: '1px solid rgba(0,0,0,0.05)' }}>
              {(() => {
                const customParams = session?.custom_attributes || session?.custom_parameters || contactData?.custom_parameters;

                if (isLoadingContact && !customParams) {
                  return (
                    <div style={{ padding: '10px', textAlign: 'center', color: '#94a3b8', fontSize: '11px' }}>
                      Loading...
                    </div>
                  );
                }



                if (customParams) {
                  const filteredEntries = Object.entries(customParams).filter(([key]) => 
                    registeredParams.some(p => p.toLowerCase() === key.toLowerCase())
                  );

                  if (filteredEntries.length > 0) {
                    return (
                      <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                        {filteredEntries.map(([key, val]) => {
                          if (val === null || val === undefined) return null;
                          const isLongOrObject = typeof val === 'object' || String(val).length > 20;
                          return (
                            <div key={key} style={{ 
                              display: 'flex', 
                              flexDirection: isLongOrObject ? 'column' : 'row',
                              justifyContent: 'space-between', 
                              alignItems: isLongOrObject ? 'flex-start' : 'center', 
                              gap: isLongOrObject ? '6px' : '0px',
                              padding: '8px 12px', 
                              background: '#f8fafc', 
                              borderRadius: '8px', 
                              border: '1px solid #e2e8f0',
                              width: '100%' 
                            }}>
                              <span style={{ fontSize: '9px', fontWeight: '700', color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.05em' }}>{key}</span>
                              <span style={{ 
                                fontSize: '11px', 
                                color: '#111827', 
                                fontWeight: '600', 
                                whiteSpace: 'pre-wrap', 
                                wordBreak: 'break-all',
                                width: '100%',
                                textAlign: isLongOrObject ? 'left' : 'right',
                                fontFamily: typeof val === 'object' ? 'monospace' : 'inherit',
                                background: typeof val === 'object' ? '#f1f5f9' : 'transparent',
                                padding: typeof val === 'object' ? '6px' : '0px',
                                borderRadius: typeof val === 'object' ? '4px' : '0px'
                              }}>
                                {typeof val === 'object' && val !== null ? JSON.stringify(val, null, 2) : String(val)}
                              </span>
                            </div>
                          );
                        })}
                      </div>
                    );
                  }
                }

                return (
                  <div style={{ fontSize: '12px', color: '#94a3b8', textAlign: 'center', padding: '10px' }}>
                    No custom parameters found
                  </div>
                );
              })()}
            </div>
          )}
        </div>

        {/* Session Details */}
        <div>
          <SectionHeader icon={Clock} title="Session Details" sectionId="session" />
          {expandedSection === 'session' && (
            <div style={{ padding: '16px', borderBottom: '1px solid rgba(0,0,0,0.05)' }}>
              <div style={{ marginBottom: '12px' }}>
                <div style={{ fontSize: '10px', fontWeight: '700', color: '#64748b', textTransform: 'uppercase', marginBottom: '4px' }}>Session ID</div>
                <div style={{ fontSize: '12px', color: '#111827', fontWeight: '500', fontFamily: 'monospace', wordBreak: 'break-all' }}>{session.session_id}</div>
              </div>
              <div style={{ marginBottom: '12px' }}>
                <div style={{ fontSize: '10px', fontWeight: '700', color: '#64748b', textTransform: 'uppercase', marginBottom: '4px' }}>Created</div>
                <div style={{ fontSize: '13px', color: '#111827', fontWeight: '500' }}>
                  {formatDate(session.created_at)} {formatTime(session.created_at) !== 'N/A' && `, ${formatTime(session.created_at)}`}
                </div>
              </div>
              <div style={{ marginBottom: '12px' }}>
                <div style={{ fontSize: '10px', fontWeight: '700', color: '#64748b', textTransform: 'uppercase', marginBottom: '4px' }}>Last Active</div>
                <div style={{ fontSize: '13px', color: '#111827', fontWeight: '500' }}>
                  {formatDate(session.updated_at)} {formatTime(session.updated_at) !== 'N/A' && `, ${formatTime(session.updated_at)}`}
                </div>
              </div>
              <div style={{ marginBottom: '12px' }}>
                <div style={{ fontSize: '10px', fontWeight: '700', color: '#64748b', textTransform: 'uppercase', marginBottom: '4px' }}>Status</div>
                <div style={{ fontSize: '13px', color: '#111827', fontWeight: '500' }}>
                  {session.handoff_status === 'active' ? 'Live' : session.handoff_status === 'requested' ? 'Requesting' : session.handoff_status === 'resolved' ? 'Resolved' : 'N/A'}
                </div>
              </div>
              <div>
                <div style={{ fontSize: '10px', fontWeight: '700', color: '#64748b', textTransform: 'uppercase', marginBottom: '4px' }}>Messages</div>
                <div style={{ fontSize: '13px', color: '#111827', fontWeight: '500' }}>{messages.length || 0}</div>
              </div>
            </div>
          )}
        </div>

        {/* System Info */}
        <div>
          <SectionHeader icon={Monitor} title="System Info" sectionId="system" />
          {expandedSection === 'system' && (
            <div style={{ padding: '16px', borderBottom: '1px solid rgba(0,0,0,0.05)' }}>
              {session.ip_address && (
                <div style={{ marginBottom: '12px' }}>
                  <div style={{ fontSize: '10px', fontWeight: '700', color: '#64748b', textTransform: 'uppercase', marginBottom: '4px' }}>IP Address</div>
                  <div style={{ fontSize: '13px', color: '#111827', fontWeight: '500', fontFamily: 'monospace' }}>{session.ip_address}</div>
                </div>
              )}
              {session.location && (
                <div style={{ marginBottom: '12px' }}>
                  <div style={{ fontSize: '10px', fontWeight: '700', color: '#64748b', textTransform: 'uppercase', marginBottom: '4px' }}>Location</div>
                  <div style={{ fontSize: '13px', color: '#111827', fontWeight: '500' }}>{session.location}</div>
                </div>
              )}
              {session.browser_info && (
                <div style={{ marginBottom: '12px' }}>
                  <div style={{ fontSize: '10px', fontWeight: '700', color: '#64748b', textTransform: 'uppercase', marginBottom: '4px' }}>Browser</div>
                  <div style={{ fontSize: '13px', color: '#111827', fontWeight: '500' }}>{session.browser_info}</div>
                </div>
              )}
              {session.os_info && (
                <div style={{ marginBottom: '12px' }}>
                  <div style={{ fontSize: '10px', fontWeight: '700', color: '#64748b', textTransform: 'uppercase', marginBottom: '4px' }}>OS</div>
                  <div style={{ fontSize: '13px', color: '#111827', fontWeight: '500' }}>{session.os_info}</div>
                </div>
              )}
              {session.device_type && (
                <div>
                  <div style={{ fontSize: '10px', fontWeight: '700', color: '#64748b', textTransform: 'uppercase', marginBottom: '4px' }}>Device</div>
                  <div style={{ fontSize: '13px', color: '#111827', fontWeight: '500' }}>{session.device_type}</div>
                </div>
              )}
            </div>
          )}
        </div>

        {/* Tags */}
        {tags.length > 0 && (
          <div>
            <SectionHeader icon={Tag} title="Tags" sectionId="tags" />
            {expandedSection === 'tags' && (
              <div style={{ padding: '16px', borderBottom: '1px solid rgba(0,0,0,0.05)' }}>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
                  {tags.map((tag, idx) => (
                    <div
                      key={idx}
                      style={{
                        padding: '6px 12px',
                        borderRadius: '16px',
                        background: '#f0f7ff',
                        color: '#2563eb',
                        fontSize: '11px',
                        fontWeight: '600',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '6px'
                      }}
                    >
                      <Zap size={12} />
                      {typeof tag === 'string' ? tag : tag.name || tag}
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Footer */}
      <div style={{
        padding: '12px 16px',
        borderTop: '1px solid rgba(0,0,0,0.05)',
        background: '#f8fafc',
        display: 'flex',
        gap: '8px',
        fontSize: '10px',
        color: '#64748b'
      }}>
     
      </div>
    </div>
  );
};

// ===== ENHANCED CRM SIDEBAR =====
const HumanAgentChatSidebar = ({
  sessions,
  activeSessionId,
  onSelectSession,
  onDeleteSession,
  isCollapsed,
  currentPage,
  totalSessions,
  onPageChange,
  pageSize,
  isLoading,
  onLogout,
  user
}) => {
  const totalPages = Math.ceil(totalSessions / pageSize);
  const [sortBy, setSortBy] = useState('latest');
  const [channelFilter, setChannelFilter] = useState('all');
  const displayedSessions = useMemo(() => {
    // 1. Filter by channel first
    const filtered = channelFilter === 'all'
      ? sessions
      : sessions.filter(s => {
          const ch = String(s.channel || '').toLowerCase();
          const sid = String(s.session_id || '').toLowerCase();
          if (channelFilter === 'whatsapp') return ch === 'whatsapp' || /^\d{7,}$/.test(sid);
          if (channelFilter === 'telegram') return ch === 'telegram' || sid.startsWith('telegram_');
          if (channelFilter === 'webchat') return !ch || ch === 'webchat';
          if (channelFilter === 'widget') return ch === 'widget' || sid.startsWith('chat_widget_');
          if (channelFilter === 'slack') return ch === 'slack' || sid.startsWith('slack_');
          if (channelFilter === 'email') return ch === 'email' || sid.startsWith('email_');
          if (channelFilter === 'freshdesk') return ch === 'freshdesk' || sid.startsWith('freshdesk_');
          if (channelFilter === 'freshchat') return ch === 'freshchat' || sid.startsWith('freshchat_');
          if (channelFilter === 'zendesk') return ch === 'zendesk' || sid.startsWith('zendesk_');
          return true;
        });
    // 2. Sort the filtered list
    return [...filtered].sort((a, b) => {
      if (sortBy === 'latest') {
        const timeA = a.updated_at ? new Date(a.updated_at < 10000000000 ? a.updated_at * 1000 : a.updated_at).getTime() : 0;
        const timeB = b.updated_at ? new Date(b.updated_at < 10000000000 ? b.updated_at * 1000 : b.updated_at).getTime() : 0;
        return timeB - timeA;
      }
      if (sortBy === 'oldest') {
        const timeA = a.updated_at ? new Date(a.updated_at < 10000000000 ? a.updated_at * 1000 : a.updated_at).getTime() : 0;
        const timeB = b.updated_at ? new Date(b.updated_at < 10000000000 ? b.updated_at * 1000 : b.updated_at).getTime() : 0;
        return timeA - timeB;
      }
      if (sortBy === 'newest') {
        const timeA = a.created_at ? new Date(a.created_at < 10000000000 ? a.created_at * 1000 : a.created_at).getTime() : 0;
        const timeB = b.created_at ? new Date(b.created_at < 10000000000 ? b.created_at * 1000 : b.created_at).getTime() : 0;
        return timeB - timeA;
      }
      if (sortBy === 'asc') {
        const nameA = (a.user_name || a.user_email || a.user_phone || 'Anonymous').toLowerCase();
        const nameB = (b.user_name || b.user_email || b.user_phone || 'Anonymous').toLowerCase();
        return nameA.localeCompare(nameB);
      }
      if (sortBy === 'desc') {
        const nameA = (a.user_name || a.user_email || a.user_phone || 'Anonymous').toLowerCase();
        const nameB = (b.user_name || b.user_email || b.user_phone || 'Anonymous').toLowerCase();
        return nameB.localeCompare(nameA);
      }
      return 0;
    });
  }, [sessions, sortBy, channelFilter]);

  const formatTime = (dateString) => {
    if (!dateString) return '';
    const timestamp = typeof dateString === 'number' && dateString < 10000000000 ? dateString * 1000 : dateString;
    const date = new Date(timestamp);
    return date.toLocaleTimeString('en-IN', {
      hour: '2-digit',
      minute: '2-digit',
      hour12: true,
      timeZone: 'Asia/Kolkata'
    });
  };



  return (
    <div style={{
      width: isCollapsed ? '70px' : '280px',
      flexShrink: 0,
      background: '#ffffff',
      borderRadius: '12px',
      border: '1px solid #e4e4e7',
      boxShadow: '0 1px 2px rgba(0,0,0,0.04)',
      display: 'flex',
      flexDirection: 'column',
      transition: 'width 0.3s ease',
      zIndex: 20,
      position: 'relative',
      overflow: 'hidden'
    }}>
      {/* Header */}
      <div style={{
        height: '56px',
        padding: isCollapsed ? '0' : '0 16px',
        borderBottom: '1px solid rgba(0,0,0,0.05)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: isCollapsed ? 'center' : 'stretch',
        background: 'linear-gradient(135deg, #83bbe1 0%, #5da9d9 100%)'
      }}>
        {!isCollapsed && (
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%' }}>
            <h2 style={{
              margin: 0,
              fontSize: '12px',
              fontWeight: '800',
              color: '#ffffff',
              textTransform: 'uppercase',
              letterSpacing: '0.08em'
            }}>Active Chats</h2>
          </div>
        )}
        {isCollapsed && <MessageSquare size={18} color="#ffffff" />}
      </div>

      {!isCollapsed && (
        <div style={{ padding: '12px', borderBottom: '1px solid rgba(0,0,0,0.05)', background: '#ffffff', display: 'flex', flexDirection: 'column', gap: '8px' }}>
          <label style={{ fontSize: '10px', fontWeight: '800', color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Filter by Channel</label>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '4px' }}>
            {[['all','All'],['whatsapp','WhatsApp'],['webchat','Webchat'],['telegram','Telegram'],['slack','Slack'],['email','Email']].map(([val, label]) => (
              <button
                key={val}
                onClick={() => setChannelFilter(val)}
                style={{
                  padding: '3px 9px',
                  borderRadius: '999px',
                  border: '1px solid',
                  borderColor: channelFilter === val ? (val === 'whatsapp' ? '#25d366' : '#2563eb') : '#e2e8f0',
                  background: channelFilter === val ? (val === 'whatsapp' ? 'rgba(37,211,102,0.1)' : 'rgba(37,99,235,0.08)') : '#f8fafc',
                  color: channelFilter === val ? (val === 'whatsapp' ? '#128C7E' : '#2563eb') : '#64748b',
                  fontSize: '9px',
                  fontWeight: '800',
                  textTransform: 'uppercase',
                  cursor: 'pointer',
                  letterSpacing: '0.05em'
                }}
              >{label}</button>
            ))}
          </div>
          <label style={{ fontSize: '10px', fontWeight: '800', color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Sort Conversations</label>
          <CustomDropdown
            options={[
              { value: "latest", label: "Latest Active (Newest First)" },
              { value: "oldest", label: "Oldest Active (Oldest First)" },
              { value: "newest", label: "Newest Created" },
              { value: "asc", label: "Name (A-Z Ascending)" },
              { value: "desc", label: "Name (Z-A Descending)" }
            ]}
            value={sortBy}
            onChange={(val) => setSortBy(val)}
            className="w-full relative z-[30]"
            buttonClassName="bg-white border border-slate-200 hover:border-slate-300 text-xs font-bold text-slate-700 px-3.5 py-2.5 rounded-xl shadow-sm min-h-[38px] justify-between cursor-pointer normal-case tracking-normal"
          />
        </div>
      )}

      {/* Chat List */}
      <div style={{
        flex: 1,
        overflowY: 'auto',
        padding: '8px',
        display: 'flex',
        flexDirection: 'column',
        gap: '4px'
      }} className="custom-scrollbar">
        {isLoading ? (
          <div style={{ padding: '40px 20px', textAlign: 'center' }}>
            <div className="animate-spin" style={{ width: '20px', height: '20px', border: '2px solid #f3f4f6', borderTop: '2px solid #2563eb', borderRadius: '50%', margin: '0 auto' }} />
          </div>
        ) : displayedSessions.length === 0 ? (
          <div style={{ padding: '40px 20px', textAlign: 'center', color: '#94a3b8', fontSize: '12px', fontWeight: '500' }}>
            No conversations found
          </div>
        ) : (
          displayedSessions.map((session) => (
            <div
              key={session.session_id}
              onClick={() => onSelectSession(session)}
              style={{
                padding: isCollapsed ? '10px' : '12px 10px',
                background: activeSessionId === session.session_id ? '#eef2ff' : 'transparent',
                borderRadius: '12px',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                gap: '10px',
                transition: 'all 0.2s ease',
                border: '1px solid',
                borderColor: activeSessionId === session.session_id ? '#c7d2fe' : 'transparent',
                position: 'relative',
                outline: 'none'
              }}
              onMouseOver={(e) => {
                if (activeSessionId !== session.session_id) {
                  e.currentTarget.style.background = '#f8fafc';
                  e.currentTarget.style.borderColor = '#e2e8f0';
                }
              }}
              onMouseOut={(e) => {
                if (activeSessionId !== session.session_id) {
                  e.currentTarget.style.background = 'transparent';
                  e.currentTarget.style.borderColor = 'transparent';
                }
              }}
            >


              {/* Avatar */}
              <div style={{
                width: isCollapsed ? '32px' : '36px',
                height: isCollapsed ? '32px' : '36px',
                borderRadius: '10px',
                border: '2px solid white',
                boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
                overflow: 'hidden',
                flexShrink: 0
              }}>
                <img
                  src={`https://api.dicebear.com/7.x/micah/svg?seed=${session.user_id || session.session_id}&backgroundColor=b6e3f4,c0aede,d1d4f9`}
                  alt="User Avatar"
                  style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                />
              </div>

              {!isCollapsed && (
                <div style={{ flex: 1, overflow: 'hidden', minWidth: 0 }}>
                  <div style={{
                    fontSize: '13px',
                    fontWeight: '600',
                    color: '#111827',
                    whiteSpace: 'nowrap',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis'
                  }}>
                    {session.user_name || session.user_email || session.user_phone || `Visitor ${session.session_id ? session.session_id.slice(-6).toUpperCase() : ''}`}
                  </div>
                  <div style={{
                    fontSize: '11px',
                    color: '#64748b',
                    marginTop: '3px',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '4px',
                    fontWeight: '500',
                    whiteSpace: 'nowrap',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis'
                  }}>
                    <Clock size={10} />
                    {formatTime(session.updated_at)}
                  </div>
                  <div style={{
                    marginTop: '4px',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '4px',
                    flexWrap: 'wrap'
                  }}>
                    {(session.handoff_status === 'active' || session.handoff_status === 'claimed' || session.assigned_to || session.is_taken_over) ? (
                      <span style={{
                        padding: '2px 6px',
                        borderRadius: '999px',
                        background: '#e0f2fe',
                        color: '#0369a1',
                        fontSize: '9px',
                        fontWeight: '800',
                        textTransform: 'uppercase'
                      }}>
                        Assigned
                      </span>
                    ) : (session.status && session.status !== 'AI') ? (
                      <span style={{
                        padding: '2px 6px',
                        borderRadius: '999px',
                        background: '#f1f5f9',
                        color: '#475569',
                        fontSize: '9px',
                        fontWeight: '800',
                        textTransform: 'uppercase'
                      }}>
                        {session.status}
                      </span>
                    ) : null}
                  </div>
                </div>
              )}
            </div>
          ))
        )}
      </div>

      {/* Pagination */}
      {!isCollapsed && totalPages > 1 && (
        <div style={{
          padding: '12px 8px',
          borderTop: '1px solid rgba(0,0,0,0.05)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          gap: '6px'
        }}>
          <button
            disabled={currentPage === 1}
            onClick={() => onPageChange(currentPage - 1)}
            style={{
              padding: '6px 10px',
              background: '#f9fafb',
              border: '1px solid #e5e7eb',
              borderRadius: '6px',
              fontSize: '10px',
              fontWeight: '700',
              color: currentPage === 1 ? '#cbd5e1' : '#64748b',
              cursor: currentPage === 1 ? 'not-allowed' : 'pointer'
            }}
          >←</button>
          <span style={{ fontSize: '10px', fontWeight: '800', color: '#111827', minWidth: '40px', textAlign: 'center' }}>{currentPage}/{totalPages}</span>
          <button
            disabled={currentPage === totalPages}
            onClick={() => onPageChange(currentPage + 1)}
            style={{
              padding: '6px 10px',
              background: '#f9fafb',
              border: '1px solid #e5e7eb',
              borderRadius: '6px',
              fontSize: '10px',
              fontWeight: '700',
              color: currentPage === totalPages ? '#cbd5e1' : '#64748b',
              cursor: currentPage === totalPages ? 'not-allowed' : 'pointer'
            }}
          >→</button>
        </div>
      )}
      {/* Sign Out Section — hidden for admins */}
      {onLogout && user?.role !== 'admin' && user?.role !== 'super_admin' && (
        <div style={{
          padding: '16px',
          borderTop: '1px solid rgba(0,0,0,0.05)',
          background: '#ffffff',
          display: 'flex',
          justifyContent: 'stretch'
        }}>
          <button
            onClick={onLogout}
            style={{
              width: '100%',
              padding: '10px',
              borderRadius: '12px',
              border: '1px solid #fecaca',
              background: '#fef2f2',
              color: '#dc2626',
              fontSize: '12px',
              fontWeight: '800',
              textTransform: 'uppercase',
              letterSpacing: '0.05em',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: '8px',
              transition: 'all 0.2s'
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.background = '#fee2e2';
              e.currentTarget.style.borderColor = '#fca5a5';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.background = '#fef2f2';
              e.currentTarget.style.borderColor = '#fecaca';
            }}
          >
            <LogOut size={14} strokeWidth={2.5} />
            <span>{!isCollapsed && 'Sign Out'}</span>
          </button>
        </div>
      )}
    </div>
  );
};

// Helper to safely parse basic Markdown and HTML formatting for rendering


// ===== MAIN AGENT CHAT INTERFACE =====
const HumanAgentChatInterface = ({ onLogout, user, hideHeader = false }) => {
  const { showToast } = useToast();

  const getActiveAiAgentId = useCallback(() => {
    const stored = localStorage.getItem('activeAiAgentId');
    if (stored && stored !== 'None' && stored !== 'null' && stored !== 'undefined') {
      return stored;
    }
    if (user) {
      if (user.role !== 'admin' && user.role !== 'super_admin') {
        const assigned = user.assigned_ai_agents || [];
        return user.ai_agent_id?.toString() || (assigned.length > 0 ? assigned[0].toString() : null);
      }
      if (user.ai_agent_id && user.ai_agent_id !== "all") {
        return user.ai_agent_id.toString();
      }
      const assigned = user.assigned_ai_agents || [];
      if (assigned.length > 0) {
        return assigned[0].toString();
      }
    }
    return null;
  }, [user]);

  const [messages, setMessages] = useState([]);
  const [historyLoaded, setHistoryLoaded] = useState(false);
  const [inputMessage, setInputMessage] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [sessionId, setSessionId] = useState(() => {
    const stored = localStorage.getItem('human_agent_interface_session_id');
    if (stored && stored !== 'None' && stored !== 'null') return stored;
    return null;
  });
  const [sessions, setSessions] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalSessions, setTotalSessions] = useState(0);
  const [isSessionsLoading, setIsSessionsLoading] = useState(false);
  const SESSIONS_PER_PAGE = 10;
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [sessionToDelete, setSessionToDelete] = useState(null);
  const [isDeletingSession, setIsDeletingSession] = useState(false);
  const [isHeaderMenuOpen, setIsHeaderMenuOpen] = useState(false);
  const [privateNoteMode, setPrivateNoteMode] = useState(false);
  
  // Ref Set to track message texts synchronously to avoid React state batching race conditions (duplicate messages)
  const addedMessageTextsRef = React.useRef(new Set());
  
  React.useEffect(() => {
    addedMessageTextsRef.current.clear();
  }, [sessionId]);
  const [, setPrivateNotes] = useState([]);
  const [localTags, setLocalTags] = useState([]);
  const [editorHtml, setEditorHtml] = useState('');
  const editorRef = useRef(null);
  const headerMenuRef = useRef(null);
  const [colorPickerOpen, setColorPickerOpen] = useState(false);
  const [currentColor, setCurrentColor] = useState('#111827');
  const [activeFormat, setActiveFormat] = useState(null);
  const [activeFormats, setActiveFormats] = useState({});
  const [emojiPickerOpen, setEmojiPickerOpen] = useState(false);
  const [attachedFile, setAttachedFile] = useState(null);

  // Handoff state
  const [isHandoffAccepted, setIsHandoffAccepted] = useState(false);
  const [isTakenOver, setIsTakenOver] = useState(false);
  const [takenOverBy, setTakenOverBy] = useState(null);
  const [takenOverByName, setTakenOverByName] = useState(null);
  const [isInactive, setIsInactive] = useState(false);
  const [isResolved, setIsResolved] = useState(false);
  const [hoveredMessageId, setHoveredMessageId] = useState(null);
  const isInactiveRef = useRef(false);

  const messagesEndRef = useRef(null);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);


  const fetchSessions = useCallback(async (page) => {
    try {
      setIsSessionsLoading(true);
      const aiAgentId = getActiveAiAgentId();
      const fetchPage = page || 1;
      // Pass escalated=false so ALL sessions (including WhatsApp, Telegram, etc.) are visible
      const data = await api.getWidgetSessions(aiAgentId, fetchPage, SESSIONS_PER_PAGE, false);
      const newSessions = data.sessions || [];
      setSessions(newSessions);
      setTotalSessions(data.total_count || 0);
      setCurrentPage(data.page || fetchPage);

      // If the currently selected session is no longer in the list (e.g. Freshchat sessions filtered out),
      // reset the UI to the clean empty state — prevents stale messages showing under "No chat selected"
      setSessionId(prev => {
        if (prev && !newSessions.some(s => s.session_id === prev)) {
          setMessages([]);
          setHistoryLoaded(false);
          localStorage.removeItem('human_agent_interface_session_id');
          return null;
        }
        return prev;
      });
    } catch (err) {
      console.error('Error fetching sessions:', err);
    } finally {
      setIsSessionsLoading(false);
    }
  }, [getActiveAiAgentId]);

  const sessionIdRef = useRef(sessionId);
  const currentPageRef = useRef(currentPage);
  const fetchSessionsRef = useRef(fetchSessions);

  useEffect(() => {
    sessionIdRef.current = sessionId;
  }, [sessionId]);

  useEffect(() => {
    currentPageRef.current = currentPage;
  }, [currentPage]);

  useEffect(() => {
    fetchSessionsRef.current = fetchSessions;
  }, [fetchSessions]);

  const loadChatHistory = useCallback(async (id = sessionId) => {
    if (!id) return;
    try {
      const aiAgentId = getActiveAiAgentId();
      const history = await api.getWidgetHistory(id, aiAgentId, true);
      
      // Clear before reloading history to prevent stale dedup keys
      addedMessageTextsRef.current.clear();
      
      if (history && history.length > 0) {
        const restored = history
          .filter(msg => msg.message && String(msg.message).trim() !== '')
          .map((msg, idx) => {
            const cleanedText = String(msg.message)
              .replace(/\s*<additional context>[\s\S]*?<\/additional context>\s*/g, '')
              .replace(/\s*\{[\s\S]*?"(?:filename|userid|category)"[\s\S]*?\}\s*/g, '')
              .replace(/[ \t]+/g, ' ')
              .trim();
            const senderRole = msg.type;
            
            // Register key to prevent future duplicates in WebSocket broadcasts
            addedMessageTextsRef.current.add(`${senderRole}_${cleanedText}`);
            
            return {
              id: `history_${idx}`,
              text: cleanedText,
              sender: senderRole,
              timestamp: new Date(typeof msg.timestamp === 'number' && msg.timestamp < 10000000000 ? msg.timestamp * 1000 : msg.timestamp).toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit', hour12: true, timeZone: 'Asia/Kolkata' }),
            };
          });
        setMessages(restored);
      } else {
        setMessages([]);
      }
      setHistoryLoaded(true);
    } catch (err) {
      console.error('Error loading chat history:', err);
    }
  }, [sessionId, getActiveAiAgentId]);

  useEffect(() => {
    fetchSessions(1);
  }, [fetchSessions]);

  useEffect(() => {
    if (sessionId) {
      loadChatHistory();
    }
  }, [sessionId, loadChatHistory]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (headerMenuRef.current && !headerMenuRef.current.contains(event.target)) {
        setIsHeaderMenuOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  const handleSelectSession = (session) => {
    const id = session.session_id;
    if (id === sessionId) return;
    setSessionId(id);
    localStorage.setItem('human_agent_interface_session_id', id);
    setMessages([]);
    setHistoryLoaded(false);

    const isAccepted = session.handoff_status === 'active' || session.handoff_status === 'claimed';
    setIsHandoffAccepted(isAccepted);
    setIsResolved(session.handoff_status === 'resolved');
    setIsTakenOver(session.is_taken_over || !!session.assigned_to || false);
    setTakenOverBy(session.taken_over_by || session.assigned_to || null);
    setTakenOverByName(session.taken_over_by_name || session.assigned_to_name || null);
    loadChatHistory(id);
  };

  const handleDeleteSession = (id) => {
    setSessionToDelete(id);
    setIsDeleteModalOpen(true);
  };

  const confirmDeleteSession = async () => {
    if (!sessionToDelete) return;
    setIsDeletingSession(true);
    try {
      const aiAgentId = getActiveAiAgentId();
      await api.deleteWidgetSession(sessionToDelete, aiAgentId);
      setSessions(prev => prev.filter(s => s.session_id !== sessionToDelete));
      if (sessionToDelete === sessionId) {
        setSessionId(null);
        setMessages([]);
      }
      setIsDeleteModalOpen(false);
    } catch (err) {
      console.error('Error deleting session:', err);
    } finally {
      setIsDeletingSession(false);
      setSessionToDelete(null);
    }
  };

  const handleTakeOverChat = async () => {
    try {
      const aiAgentId = getActiveAiAgentId();
      await api.post(`/widget/sessions/${sessionId}/takeover`, {}, { aiAgentId });
      setIsHandoffAccepted(true);
      setIsResolved(false);
      setIsTakenOver(true);
      fetchSessions(currentPage);
      setIsHeaderMenuOpen(false);
      showToast('Chat taken over successfully');
    } catch (err) {
      console.error('Error taking over chat:', err);
      showToast('Failed to take over chat');
    }
  };



  // WebSocket integration
  useEffect(() => {
    if (!user) return;

    let socket = null;
    let reconnectTimeout = null;
    let inactivityTimeout = null;
    let isComponentMounted = true;

    const resetInactivityTimeout = () => {
      if (inactivityTimeout) clearTimeout(inactivityTimeout);
      if (isInactiveRef.current) {
        setIsInactive(false);
        isInactiveRef.current = false;
        connectWebSocket();
      }
      inactivityTimeout = setTimeout(() => {
        setIsInactive(true);
        isInactiveRef.current = true;
        if (socket) socket.close();
      }, 1800000);
    };

    const events = ['mousemove', 'keypress', 'click', 'scroll'];
    events.forEach(event => window.addEventListener(event, resetInactivityTimeout));

    resetInactivityTimeout();

    const connectWebSocket = () => {
      if (!isComponentMounted) return;
      if (isInactiveRef.current) return;

      const aiAgentId = getActiveAiAgentId();
      let token = null;
      const storedUser = sessionStorage.getItem('user');
      if (storedUser) {
        try {
          const userData = JSON.parse(storedUser);
          token = userData.token;
        } catch (e) {
          console.error('Error parsing user data:', e);
        }
      }

      if (!token) return;

      const apiBase = process.env.REACT_APP_API_BASE_URL || window.location.origin;
      const isHttps = apiBase.startsWith('https://') || window.location.protocol === 'https:';
      const protocol = isHttps ? 'wss:' : 'ws:';
      const wsHost = apiBase.replace(/^https?:\/\//, '').replace(/\/api$/, '');
      const wsUrl = `${protocol}//${wsHost}/api/ws/ai-agents?ai_agent_id=${aiAgentId}&token=${token}`;

      socket = new WebSocket(wsUrl);

      const lastEventRef = { id: null, type: null, time: 0 };

      socket.onmessage = (event) => {
        try {
          const data = JSON.parse(event.data);
          console.log('🔌 Human Agent WS Message Received:', data);
          const now = Date.now();
          if (data.session_id === lastEventRef.id && data.type === lastEventRef.type && (now - lastEventRef.time) < 100) return;
          lastEventRef.id = data.session_id;
          lastEventRef.type = data.type;
          lastEventRef.time = now;

          const currentActiveSessionId = sessionIdRef.current;

          switch (data.type) {
            case 'SESSION_STATUS':
              if (data.session_id) {
                if (data.status === 'requested') {
                  setSessions(prev => prev.map(s =>
                    s.session_id === data.session_id
                      ? { ...s, handoff_status: 'requested', last_reason: data.reason }
                      : s
                  ));
                  if (data.session_id === currentActiveSessionId) {
                    // Removed unused handoff states
                  }
                }
                if (fetchSessionsRef.current) fetchSessionsRef.current(currentPageRef.current);
              }
              break;
            case 'HANDOFF_REQUESTED':
              if (fetchSessionsRef.current) fetchSessionsRef.current(currentPageRef.current);
              break;
            case 'NEW_MESSAGE':
              if (data.session_id === currentActiveSessionId) {
                const cleanedText = String(data.message.content)
                  .replace(/\s*<additional context>[\s\S]*?<\/additional context>\s*/g, '')
                  .replace(/\s*\{[\s\S]*?"(?:filename|userid|category)"[\s\S]*?\}\s*/g, '')
                  .replace(/[ \t]+/g, ' ')
                  .trim();
                const incomingRole = data.message.role;
                const dedupKey = `${incomingRole}_${cleanedText}`;
                
                // Synchronous check & set to immediately abort duplicate WebSocket events in the same event loop tick
                if (addedMessageTextsRef.current.has(dedupKey)) {
                  break;
                }
                addedMessageTextsRef.current.add(dedupKey);
                
                setMessages(prev => {
                  const exists = prev.some(m => m.text.trim() === cleanedText && m.sender === incomingRole);
                  if (exists) return prev;
                  return [...prev, {
                    id: Date.now() + Math.random(),
                    text: cleanedText,
                    sender: incomingRole,
                    timestamp: new Date().toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit', hour12: true, timeZone: 'Asia/Kolkata' })
                  }];
                });
              }
              if (fetchSessionsRef.current) fetchSessionsRef.current(currentPageRef.current);
              break;
            case 'PRIVATE_NOTE':
              if (data.session_id === currentActiveSessionId) {
                setMessages(prev => {
                  const cleanedText = String(data.message.content).trim();
                  const exists = prev.some(m => m.text.trim() === cleanedText && m.sender === 'private_note');
                  if (exists) return prev;
                  return [...prev, {
                    id: Date.now() + Math.random(),
                    text: cleanedText,
                    sender: 'private_note',
                    timestamp: new Date().toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit', hour12: true, timeZone: 'Asia/Kolkata' })
                  }];
                });
              }
              if (fetchSessionsRef.current) fetchSessionsRef.current(currentPageRef.current);
              break;
            case 'SESSION_CLAIMED':
              if (data.session_id === currentActiveSessionId) {
                setIsHandoffAccepted(true);
                if (data.assigned_to) setTakenOverBy(data.assigned_to);
                if (data.assigned_to_name) setTakenOverByName(data.assigned_to_name);
              }
              if (fetchSessionsRef.current) fetchSessionsRef.current(currentPageRef.current);
              break;
            case 'SESSION_RESOLVED':
              if (data.session_id === currentActiveSessionId) {
                setIsHandoffAccepted(false);
                setIsResolved(true);
              }
              if (fetchSessionsRef.current) fetchSessionsRef.current(currentPageRef.current);
              break;
            case 'SESSION_TAKEOVER':
              if (data.session_id === currentActiveSessionId) {
                setIsTakenOver(true);
                setTakenOverBy(data.taken_over_by);
                setTakenOverByName(data.taken_over_by_name);
              }
              if (fetchSessionsRef.current) fetchSessionsRef.current(currentPageRef.current);
              break;

            default:
              break;
          }
        } catch (err) {
          console.error('WS Parsing Error:', err);
        }
      };

      socket.onclose = () => {
        if (!isComponentMounted) return;
        if (isInactiveRef.current) return;
        reconnectTimeout = setTimeout(connectWebSocket, 3000);
      };
    };

    connectWebSocket();

    return () => {
      isComponentMounted = false;
      if (socket) socket.close();
      if (reconnectTimeout) clearTimeout(reconnectTimeout);
      if (inactivityTimeout) clearTimeout(inactivityTimeout);
      events.forEach(event => window.removeEventListener(event, resetInactivityTimeout));
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  const sendMessage = async () => {
    if ((!inputMessage.trim() && !attachedFile) || isLoading) return;
    if (!sessionId) {
      showToast('Select a chat to send a message.');
      return;
    }

    const currentInput = inputMessage;
    const currentHtml = editorHtml;
    const fileToUpload = attachedFile;
    
    setInputMessage('');
    setEditorHtml('');
    setAttachedFile(null);
    if (editorRef.current) editorRef.current.innerHTML = '';

    if (privateNoteMode) {
      setIsLoading(true);
      try {
        const aiAgentId = getActiveAiAgentId();
        await api.sendPrivateNote(sessionId, currentInput, aiAgentId);

        const note = {
          id: `private_${Date.now()}`,
          text: currentInput,
          sender: 'private_note',
          timestamp: new Date().toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit', hour12: true, timeZone: 'Asia/Kolkata' })
        };
        addedMessageTextsRef.current.add(`private_note_${currentInput.trim()}`);
        setPrivateNotes(prev => [...prev, note]);
        setMessages(prev => {
          const exists = prev.some(m => m.text.trim() === currentInput.trim() && m.sender === 'private_note');
          if (exists) return prev;
          return [...prev, note];
        });
        showToast('Private note saved to this chat.');
      } catch (err) {
        console.error('Error sending private note:', err);
        showToast('Failed to save private note.');
        setInputMessage(currentInput);
        if (editorRef.current) editorRef.current.innerHTML = currentHtml;
      } finally {
        setIsLoading(false);
      }
      return;
    }

    if (!isHandoffAccepted) return;

    setIsLoading(true);

    try {
      let imageBase64 = null;
      let fileBase64 = null;

      if (fileToUpload) {
        const fileReader = new FileReader();
        const readPromise = new Promise((resolve, reject) => {
          fileReader.onload = () => resolve(fileReader.result);
          fileReader.onerror = (e) => reject(e);
        });
        fileReader.readAsDataURL(fileToUpload);
        const base64Data = await readPromise;

        if (fileToUpload.type.startsWith('image/')) {
          imageBase64 = base64Data;
        } else {
          fileBase64 = base64Data;
        }
      }

      const aiAgentId = getActiveAiAgentId();
      await api.post('/widget/chat/agent-message', {
        session_id: sessionId,
        message: currentInput,
        image: imageBase64,
        file: fileBase64,
        metadata: fileToUpload ? { filename: fileToUpload.name } : {}
      }, { aiAgentId });

      // Reload chat history to pull the newly uploaded file/image with its correct server path
      await loadChatHistory(sessionId);
      fetchSessions(currentPage);
    } catch (error) {
      console.error('Chat error:', error);
      setInputMessage(currentInput);
      setEditorHtml(currentHtml);
      if (editorRef.current) editorRef.current.innerHTML = currentHtml;
    } finally {
      setIsLoading(false);
    }
  };




  const handleResolveHandoff = useCallback(async () => {
    try {
      const aiAgentId = getActiveAiAgentId();
      await api.post('/widget/handoff/resolve', { session_id: sessionId }, { aiAgentId });
      setIsHandoffAccepted(false);
      setIsResolved(true);
      fetchSessions(currentPage);
    } catch (err) {
      console.error('Resolve error:', err);
    }
  }, [sessionId, currentPage, fetchSessions, getActiveAiAgentId]);

  const getTagLabel = (tag) => {
    if (!tag) return '';
    return typeof tag === 'string' ? tag : tag.name || String(tag);
  };

  const convertHtmlToMarkdown = (html) => {
    if (!html) return '';
    let md = html;
    md = md.replace(/<div><br><\/div>/gi, '\n');
    md = md.replace(/<div[^>]*>/gi, '\n');
    md = md.replace(/<\/div>/gi, '');
    md = md.replace(/<p[^>]*>/gi, '');
    md = md.replace(/<\/p>/gi, '\n');
    md = md.replace(/<br\s*\/?>/gi, '\n');
    md = md.replace(/<b[^>]*>([\s\S]*?)<\/b>/gi, '**$1**');
    md = md.replace(/<strong[^>]*>([\s\S]*?)<\/strong>/gi, '**$1**');
    md = md.replace(/<i[^>]*>([\s\S]*?)<\/i>/gi, '*$1*');
    md = md.replace(/<em[^>]*>([\s\S]*?)<\/em>/gi, '*$1*');
    md = md.replace(/<strike[^>]*>([\s\S]*?)<\/strike>/gi, '~~$1~~');
    md = md.replace(/<s[^>]*>([\s\S]*?)<\/s>/gi, '~~$1~~');
    md = md.replace(/<ul[^>]*>([\s\S]*?)<\/ul>/gi, (match, p1) => {
      return '\n' + p1.replace(/<li[^>]*>([\s\S]*?)<\/li>/gi, '- $1\n') + '\n';
    });
    md = md.replace(/<ol[^>]*>([\s\S]*?)<\/ol>/gi, (match, p1) => {
      let count = 1;
      return '\n' + p1.replace(/<li[^>]*>([\s\S]*?)<\/li>/gi, (m, p) => `${count++}. ${p}\n`) + '\n';
    });
    // Mask span color styles, font color tags, and underline tags so they are not stripped
    md = md.replace(/<span style="color:\s*([^;"]+);?">([\s\S]*?)<\/span>/gi, '__SPAN_COLOR_START_($1)__$2__SPAN_COLOR_END__');
    md = md.replace(/<span style='color:\s*([^;']+);?'>([\s\S]*?)<\/span>/gi, '__SPAN_COLOR_START_($1)__$2__SPAN_COLOR_END__');
    md = md.replace(/<font color="([^"]+)">([\s\S]*?)<\/font>/gi, '__FONT_COLOR_START_($1)__$2__FONT_COLOR_END__');
    md = md.replace(/<font color='([^']+)'>([\s\S]*?)<\/font>/gi, '__FONT_COLOR_START_($1)__$2__FONT_COLOR_END__');
    md = md.replace(/<u[^>]*>([\s\S]*?)<\/u>/gi, '__UNDERLINE_START__$1__UNDERLINE_END__');

    md = md.replace(/<[^>]+>/g, '');

    // Restore color and underline tags
    md = md.replace(/__SPAN_COLOR_START_\(([^)]+)\)__/g, '<span style="color:$1">');
    md = md.replace(/__SPAN_COLOR_END__/g, '</span>');
    md = md.replace(/__FONT_COLOR_START_\(([^)]+)\)__/g, '<font color="$1">');
    md = md.replace(/__FONT_COLOR_END__/g, '</font>');
    md = md.replace(/__UNDERLINE_START__/g, '<u>');
    md = md.replace(/__UNDERLINE_END__/g, '</u>');

    md = md.replace(/&nbsp;/g, ' ')
      .replace(/&lt;/g, '<')
      .replace(/&gt;/g, '>')
      .replace(/&amp;/g, '&');
    md = md.replace(/\n{3,}/g, '\n\n');
    return md.trim();
  };

  const updateFormattingState = () => {
    if (!editorRef.current) return;
    setActiveFormats({
      bold: document.queryCommandState('bold'),
      italic: document.queryCommandState('italic'),
      underline: document.queryCommandState('underline'),
      strikeThrough: document.queryCommandState('strikeThrough'),
      insertUnorderedList: document.queryCommandState('insertUnorderedList'),
      insertOrderedList: document.queryCommandState('insertOrderedList')
    });
  };

  const syncEditorState = () => {
    const html = editorRef.current?.innerHTML || '';
    const text = convertHtmlToMarkdown(html);
    setEditorHtml(html);
    setInputMessage(text);
    updateFormattingState();
  };

  const execEditorCommand = (command, value = null) => {
    if (!editorRef.current || !sessionId || (!isHandoffAccepted && !privateNoteMode)) return;
    editorRef.current.focus();
    document.execCommand(command, false, value);
    if (command !== 'foreColor') setActiveFormat(null);
    syncEditorState();
  };

  const applyColor = (color) => {
    setCurrentColor(color);
    execEditorCommand('foreColor', color);
    setColorPickerOpen(false);
  };

  const clearFormatting = () => {
    if (!editorRef.current || !sessionId || (!isHandoffAccepted && !privateNoteMode)) return;
    editorRef.current.focus();
    document.execCommand('removeFormat', false, null);
    document.execCommand('unlink', false, null);

    if (document.queryCommandState('insertUnorderedList')) {
      document.execCommand('insertUnorderedList', false, null);
    }
    if (document.queryCommandState('insertOrderedList')) {
      document.execCommand('insertOrderedList', false, null);
    }

    setCurrentColor('#111827');
    setActiveFormat(null);
    syncEditorState();
  };

  const handleAddTag = () => {
    const tagValue = window.prompt('Enter new tag name:');
    if (!tagValue || !tagValue.trim()) return;

    const sanitized = tagValue.replace(/[^a-zA-Z0-9-_ ]+/g, '').trim();
    if (!sanitized) {
      showToast('Tag must contain letters or numbers.');
      return;
    }
    setLocalTags(prev => prev.includes(sanitized) ? prev : [...prev, sanitized]);
    showToast(`Tag added: ${sanitized}`);
  };

  const toggleHeaderMenu = () => {
    setIsHeaderMenuOpen(prev => !prev);
  };

  useEffect(() => {
    const currentSession = sessions.find(s => s.session_id === sessionId);
    if (currentSession) {
      setIsHandoffAccepted(currentSession.handoff_status === 'active');
      setIsResolved(currentSession.handoff_status === 'resolved');
      setIsTakenOver(currentSession.is_taken_over || !!currentSession.assigned_to || false);
      setTakenOverBy(currentSession.taken_over_by || currentSession.assigned_to || null);
      setTakenOverByName(currentSession.taken_over_by_name || currentSession.assigned_to_name || null);
    }
  }, [sessionId, sessions]);

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  };

  const handlePaste = (e) => {
    const isTakenOverByOther = isTakenOver && 
      String(takenOverBy) !== String(user?.id) && 
      String(takenOverBy) !== String(user?.username) && 
      String(takenOverBy) !== String(user?.human_agent_id);
    const isInputDisabled = !sessionId || isResolved || (!isHandoffAccepted && !privateNoteMode) || isTakenOverByOther;

    if (isInputDisabled) return;
    const items = e.clipboardData.items;
    for (let i = 0; i < items.length; i++) {
      if (items[i].type.indexOf('image') !== -1) {
        const file = items[i].getAsFile();
        if (file) {
          e.preventDefault();
          const renamedFile = new File([file], file.name || `pasted-image-${Date.now()}.png`, { type: file.type });
          setAttachedFile(renamedFile);
          showToast(`Image pasted: ${renamedFile.name}`);
        }
      }
    }
  };

  const currentSession = sessions.find(s => s.session_id === sessionId);
  const combinedTags = Array.from(new Set([...(currentSession?.tags || []).map(getTagLabel), ...localTags]));

  return (
    <div style={{ height: (user?.role === 'admin' || user?.role === 'super_admin') ? '100%' : '100vh', display: 'flex', flexDirection: 'column', background: '#fafafa', overflow: 'hidden' }}>

      {/* Top Navbar — only shown for human_agent role (admins get navbar from PageWrapper) */}
      {user?.role !== 'admin' && (
        <div style={{
          height: '56px',
          flexShrink: 0,
          background: 'linear-gradient(135deg, #83bbe1 0%, #5da9d9 100%)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          padding: '0 24px',
          boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
          zIndex: 30
        }}>
          <h1 style={{ margin: 0, fontSize: '15px', fontWeight: '900', color: '#ffffff', textTransform: 'uppercase', letterSpacing: '0.12em' }}>
            Active Chats
          </h1>
          <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
            {user?.name && (
              <span style={{ fontSize: '12px', fontWeight: '600', color: 'rgba(255,255,255,0.85)' }}>
                {user.name}
              </span>
            )}
          </div>
        </div>
      )}

        {/* 3-Panel Layout */}
      <div style={{ flex: 1, display: 'flex', overflow: 'hidden', gap: '8px', padding: '8px', background: '#fafafa' }}>
        {/* LEFT - Active Chats */}
        <HumanAgentChatSidebar
          sessions={sessions}
          activeSessionId={sessionId}
          onSelectSession={handleSelectSession}
          onDeleteSession={handleDeleteSession}
          isCollapsed={false}
          currentPage={currentPage}
          totalSessions={totalSessions}
          onPageChange={fetchSessions}
          pageSize={SESSIONS_PER_PAGE}
          isLoading={isSessionsLoading}
          onLogout={onLogout}
          user={user}
        />

        {/* CENTER - Chat Window */}
        <div style={{
          flex: 1,
          display: 'flex',
          flexDirection: 'column',
          background: '#ffffff',
          position: 'relative',
          minWidth: 0,
          borderRadius: '12px',
          border: '1px solid #e4e4e7',
          boxShadow: '0 1px 2px rgba(0,0,0,0.04)',
          overflow: 'hidden'
        }}>

          {/* Banners */}
          {isInactive && (
            <div style={{
              padding: '12px 20px',
              background: '#fffbeb',
              borderBottom: '1px solid #fef3c7',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              animation: 'slideDown 0.4s ease',
              zIndex: 20
            }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                <div style={{ width: '32px', height: '32px', borderRadius: '8px', background: '#f59e0b', display: 'flex', alignItems: 'center', justifycontent: 'center', color: 'white' }}>
                  <AlertTriangle size={16} />
                </div>
                <div>
                  <h4 style={{ margin: 0, fontSize: '11px', fontWeight: '700', textTransform: 'uppercase', letterSpacing: '0.05em', color: '#92400e' }}>Connection Paused</h4>
                  <p style={{ margin: '2px 0 0', fontSize: '12px', color: '#b45309', fontWeight: '500' }}>Disconnected due to inactivity. Click Reconnect to resume.</p>
                </div>
              </div>
              <button
                onClick={() => window.dispatchEvent(new Event('mousemove'))}
                style={{ padding: '6px 14px', background: '#d97706', color: 'white', border: 'none', borderRadius: '8px', fontSize: '11px', fontWeight: '700', cursor: 'pointer' }}
              >
                Reconnect
              </button>
            </div>
          )}

          {/* Assigned / Active Banner */}
          {isHandoffAccepted && !isResolved && (
            <div style={{
              padding: '8px 20px',
              background: '#f4f4f5',
              borderBottom: '1px solid #e4e4e7',
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
              zIndex: 19
            }}>
              <User size={13} color="#52525b" />
              <span style={{ fontSize: '11px', fontWeight: '600', color: '#52525b' }}>
                {takenOverByName || currentSession?.assigned_to_name
                  ? `Assigned to: ${takenOverByName || currentSession?.assigned_to_name || currentSession?.assigned_to}`
                  : 'Session is being handled by a human agent'}
              </span>
            </div>
          )}

          {/* Chat Window Header */}
          <div style={{
            minHeight: '56px',
            padding: '12px 20px',
            borderBottom: '1px solid rgba(93,169,217,0.3)',
            background: 'linear-gradient(135deg, #83bbe1 0%, #5da9d9 100%)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: '16px',
            position: 'relative',
            zIndex: 15
          }}>
            <div style={{ minWidth: 0, flex: 1 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap' }}>
                <div style={{ fontSize: '14px', fontWeight: '800', color: '#ffffff', minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                  {currentSession ? `Chat • ${currentSession.user_name || currentSession.user_email || currentSession.user_phone || `Visitor ${currentSession.session_id ? currentSession.session_id.slice(-6).toUpperCase() : ''}`}` : 'No chat selected'}
                </div>
                {currentSession && (
                  <span style={{
                    padding: '3px 8px',
                    borderRadius: '999px',
                    background: 'rgba(255,255,255,0.25)',
                    color: '#ffffff',
                    fontSize: '10px',
                    fontWeight: '700',
                    textTransform: 'uppercase',
                    border: '1px solid rgba(255,255,255,0.35)'
                  }}>
                    {currentSession.handoff_status === 'active' ? 'Live' : currentSession.handoff_status === 'requested' ? 'Requested' : currentSession.handoff_status === 'resolved' ? 'Resolved' : ''}
                  </span>
                )}
                {(isTakenOver || (currentSession && (currentSession.assigned_to || currentSession.assigned_to_name))) && (
                  <span style={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: '4px',
                    padding: '3px 8px',
                    borderRadius: '999px',
                    background: 'rgba(255,255,255,0.2)',
                    color: '#ffffff',
                    fontSize: '10px',
                    fontWeight: '600',
                    border: '1px solid rgba(255,255,255,0.3)'
                  }}>
                    <User size={11} color="#ffffff" />
                    Assigned: {takenOverByName || (currentSession && (currentSession.assigned_to_name || currentSession.assigned_to)) || 'Human Agent'}
                  </span>
                )}
              </div>

              {combinedTags.length > 0 && (
                <div style={{ marginTop: '6px', display: 'flex', flexWrap: 'wrap', gap: '4px' }}>
                  {combinedTags.map((tag, idx) => (
                    <div key={idx} style={{ display: 'inline-flex', alignItems: 'center', gap: '3px', padding: '2px 7px', borderRadius: '999px', background: 'rgba(255,255,255,0.2)', color: '#ffffff', fontSize: '10px', fontWeight: '600', border: '1px solid rgba(255,255,255,0.3)' }}>
                      <Zap size={10} color="#ffffff" />
                      {tag}
                    </div>
                  ))}
                </div>
              )}
            </div>

            {currentSession && !isResolved && (
              <div ref={headerMenuRef} style={{ position: 'relative' }}>
                <button
                  onClick={toggleHeaderMenu}
                  style={{
                    width: '38px',
                    height: '38px',
                    borderRadius: '12px',
                    border: '1px solid #e5e7eb',
                    background: 'white',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    cursor: 'pointer'
                  }}
                  title="More options"
                >
                  <MoreVertical size={16} color="#64748b" />
                </button>
                {isHeaderMenuOpen && (
                  <div style={{
                    position: 'absolute',
                    right: 0,
                    top: '100%',
                    marginTop: '8px',
                    minWidth: '200px',
                    background: '#ffffff',
                    border: '1px solid #e5e7eb',
                    borderRadius: '12px',
                    boxShadow: '0 10px 25px rgba(0,0,0,0.08)',
                    zIndex: 50,
                    overflow: 'hidden'
                  }}>
                    {(user?.role === 'admin' || user?.role === 'super_admin') && !isTakenOver && (
                      <button
                        onClick={() => { handleTakeOverChat(); setIsHeaderMenuOpen(false); }}
                        style={{
                          width: '100%',
                          textAlign: 'left',
                          padding: '12px 16px',
                          border: 'none',
                          background: 'transparent',
                          cursor: 'pointer',
                          color: '#d97706',
                          fontWeight: '600',
                          fontSize: '13px',
                          borderBottom: '1px solid #f1f5f9'
                        }}
                        onMouseOver={(e) => e.currentTarget.style.background = '#f8fafc'}
                        onMouseOut={(e) => e.currentTarget.style.background = 'transparent'}
                      >
                        Take Over Chat
                      </button>
                    )}

                    <button
                      onClick={() => {
                        handleResolveHandoff();
                        setIsHeaderMenuOpen(false);
                      }}
                      style={{
                        width: '100%',
                        textAlign: 'left',
                        padding: '12px 16px',
                        border: 'none',
                        background: 'transparent',
                        cursor: 'pointer',
                        color: '#ef4444',
                        fontWeight: '600',
                        fontSize: '13px'
                      }}
                      onMouseOver={(e) => e.currentTarget.style.background = '#fff5f5'}
                      onMouseOut={(e) => e.currentTarget.style.background = 'transparent'}
                    >
                      Resolve session
                    </button>
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Messages */}
          <div style={{ flex: 1, overflowY: 'auto', padding: '32px 24px' }} className="custom-scrollbar">
            {!sessionId ? (
              <div style={{ height: '100%', display: 'flex', justifyContent: 'center', alignItems: 'center', color: '#94a3b8', fontSize: '14px' }}>
                Select a chat to start
              </div>
            ) : !historyLoaded ? (
              <div style={{ height: '100%', display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
                <div className="animate-spin" style={{ width: '30px', height: '30px', border: '3px solid #f3f4f6', borderTop: '3px solid #2563eb', borderRadius: '50%' }} />
              </div>
            ) : (
              (() => {
                let renderedSeparator = false;
                return messages.map((message, idx) => {
                  const isFirstHumanMessage = !renderedSeparator && message.sender === 'human_agent';
                  if (isFirstHumanMessage) {
                    renderedSeparator = true;
                  }
                  return (
                    <React.Fragment key={message.id}>
                      {isFirstHumanMessage && (
                        <div style={{ display: 'flex', alignItems: 'center', margin: '24px 0', width: '100%' }}>
                          <div style={{ flex: 1, borderTop: '1px solid #e2e8f0' }}></div>
                          <span style={{ margin: '0 16px', padding: '6px 16px', fontSize: '10px', fontWeight: '800', letterSpacing: '0.05em', color: '#64748b', background: '#f8fafc', borderRadius: '999px', border: '1px solid #e2e8f0', boxShadow: '0 1px 2px rgba(0,0,0,0.05)', textTransform: 'uppercase' }}>
                            Connected to Human Agent
                          </span>
                          <div style={{ flex: 1, borderTop: '1px solid #e2e8f0' }}></div>
                        </div>
                      )}
                      <div style={{ display: 'flex', justifyContent: (message.sender === 'user' || message.sender === 'human') ? 'flex-start' : 'flex-end', marginBottom: '24px', alignItems: 'flex-end', gap: '12px' }}>
                        {(message.sender === 'user' || message.sender === 'human') && (
                          <div style={{
                            width: '32px',
                            height: '32px',
                            borderRadius: '8px',
                            border: '1px solid #e5e7eb',
                            overflow: 'hidden',
                            flexShrink: 0
                          }}>
                            <img
                              src={`https://api.dicebear.com/7.x/micah/svg?seed=${currentSession?.user_id || sessionId}&backgroundColor=b6e3f4,c0aede,d1d4f9`}
                              alt="User Avatar"
                              style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                            />
                          </div>
                        )}
                        <div
                          style={{ maxWidth: '70%', position: 'relative' }}
                          onMouseEnter={() => setHoveredMessageId(message.id)}
                          onMouseLeave={() => setHoveredMessageId(null)}
                        >
                          <div style={{
                            background: message.sender === 'private_note' ? '#f8fafc' : (message.sender === 'user' || message.sender === 'human') ? '#ffffff' : '#eff6ff',
                            padding: '12px 16px',
                            borderRadius: '16px',
                            boxShadow: '0 2px 8px rgba(0,0,0,0.05)',
                            border: '1px solid rgba(0,0,0,0.05)',
                            wordBreak: 'break-word',
                            wordWrap: 'break-word',
                            overflowWrap: 'break-word',
                            whiteSpace: 'pre-wrap'
                          }}>
                            {message.sender === 'private_note' && (
                              <div style={{ fontSize: '10px', fontWeight: '800', textTransform: 'uppercase', color: '#0f172a', marginBottom: '4px' }}>
                                Private Note
                              </div>
                            )}
                            {message.sender !== 'user' && message.sender !== 'human' && message.sender !== 'private_note' && (
                              <div style={{ fontSize: '10px', fontWeight: '800', textTransform: 'uppercase', color: '#2563eb', marginBottom: '4px' }}>
                                {message.sender === 'human_agent' ? 'Human Agent' : (message.sender === 'assistant' || message.sender === 'bot') ? 'AI Agent' : message.sender}
                              </div>
                            )}
                            <div className="markdown-content" style={{ fontSize: '14px', lineHeight: '1.5', color: '#111827', wordBreak: 'break-word', wordWrap: 'break-word', overflowWrap: 'break-word' }}>
                              <ReactMarkdown>{message.text}</ReactMarkdown>
                            </div>
                          </div>
                          {hoveredMessageId === message.id && (
                            <div style={{
                              position: 'absolute',
                              bottom: '100%',
                              right: (message.sender === 'user' || message.sender === 'human') ? 'auto' : '0',
                              left: (message.sender === 'user' || message.sender === 'human') ? '0' : 'auto',
                              marginBottom: '6px',
                              background: '#1e293b',
                              color: '#ffffff',
                              padding: '4px 8px',
                              borderRadius: '4px',
                              fontSize: '10px',
                              whiteSpace: 'nowrap',
                              zIndex: 10,
                              boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
                              pointerEvents: 'none'
                            }}>
                              {message.timestamp}
                            </div>
                          )}
                        </div>
                        {message.sender !== 'user' && message.sender !== 'human' && (
                          <div style={{
                            width: '32px',
                            height: '32px',
                            borderRadius: '8px',
                            background: message.sender === 'human_agent' ? 'transparent' : 'linear-gradient(135deg, #83bbe1 0%, #5da9d9 100%)',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            fontSize: '12px',
                            fontWeight: '600',
                            color: 'white',
                            flexShrink: 0,
                            overflow: 'hidden',
                            border: message.sender === 'human_agent' ? '1px solid #e5e7eb' : 'none'
                          }}>
                            {message.sender === 'human_agent' ? (
                              <img src="/agent-icon.png" alt="Human Agent" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                            ) : (
                              '🤖'
                            )}
                          </div>
                        )}
                      </div>
                    </React.Fragment>
                  );
                });
              })()
            )}
            {currentSession && currentSession.handoff_status === 'requested' && !isResolved && (
              <div style={{ display: 'flex', alignItems: 'center', margin: '24px 0', width: '100%' }}>
                <div style={{ flex: 1, borderTop: '1px solid #fde68a' }}></div>
                <span style={{ margin: '0 16px', padding: '6px 16px', fontSize: '10px', fontWeight: '800', letterSpacing: '0.05em', color: '#b45309', background: '#fffbeb', borderRadius: '999px', border: '1px solid #fde68a', boxShadow: '0 1px 2px rgba(0,0,0,0.05)', textTransform: 'uppercase' }}>
                  Handoff Requested
                </span>
                <div style={{ flex: 1, borderTop: '1px solid #fde68a' }}></div>
              </div>
            )}

            {isResolved && (
              <div style={{ display: 'flex', alignItems: 'center', margin: '24px 0', width: '100%' }}>
                <div style={{ flex: 1, borderTop: '1px solid #e2e8f0' }}></div>
                <span style={{ margin: '0 16px', padding: '6px 16px', fontSize: '10px', fontWeight: '800', letterSpacing: '0.05em', color: '#64748b', background: '#f8fafc', borderRadius: '999px', border: '1px solid #e2e8f0', boxShadow: '0 1px 2px rgba(0,0,0,0.05)', textTransform: 'uppercase' }}>
                  Conversation Resolved
                </span>
                <div style={{ flex: 1, borderTop: '1px solid #e2e8f0' }}></div>
              </div>
            )}
            <div ref={messagesEndRef} />
          </div>

          {/* Input Area */}
          <div style={{ padding: '12px 12px 4px 12px', borderTop: '1px solid rgba(0,0,0,0.05)', background: 'white' }}>
            {(() => {
              const isTakenOverByOther = isTakenOver && 
                String(takenOverBy) !== String(user?.id) && 
                String(takenOverBy) !== String(user?.username) && 
                String(takenOverBy) !== String(user?.human_agent_id);
              const isInputDisabled = !sessionId || isResolved || (!isHandoffAccepted && !privateNoteMode) || isTakenOverByOther;
              const inputPlaceholder = isResolved ? 'This conversation is resolved.' :
                isTakenOverByOther ? `This chat is currently managed by ${takenOverByName || 'Admin'}.` :
                  sessionId ? (privateNoteMode ? 'Type a private note...' : (isHandoffAccepted ? 'Type your message...' : 'Take over session to send messages')) : 'Select a conversation first';
              return (
                <div style={{ display: 'flex', gap: '12px', alignItems: 'flex-start' }}>
                  <div style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: '12px' }}>
                    <div style={{
                      border: '1px solid #e2e8f0',
                      borderRadius: '12px',
                      background: sessionId && (isHandoffAccepted || privateNoteMode) ? '#ffffff' : '#f8fafc',
                      boxShadow: '0 1px 3px rgba(0,0,0,0.05)',
                      display: 'flex',
                      flexDirection: 'column',
                      overflow: 'hidden'
                    }}>
                      {/* Unified Top Toolbar */}
                      <div style={{
                        display: 'flex',
                        flexWrap: 'wrap',
                        gap: '4px',
                        alignItems: 'center',
                        padding: '6px 12px',
                        background: '#f8fafc',
                        borderBottom: '1px solid #e2e8f0'
                      }}>
                        {[
                          { command: 'bold', icon: Bold, label: 'Bold', active: activeFormats.bold },
                          { command: 'italic', icon: Italic, label: 'Italic', active: activeFormats.italic },
                          { command: 'underline', icon: Underline, label: 'Underline', active: activeFormats.underline },
                          { command: 'strikeThrough', icon: Strikethrough, label: 'Strike Through', active: activeFormats.strikeThrough },
                          { command: 'insertUnorderedList', icon: List, label: 'Bullet List', active: activeFormats.insertUnorderedList },
                          { command: 'insertOrderedList', icon: ListOrdered, label: 'Numbered List', active: activeFormats.insertOrderedList }
                        ].map((btn) => {
                          const IconComponent = btn.icon;
                          return (
                            <button
                              key={btn.command}
                              type="button"
                              onClick={() => execEditorCommand(btn.command)}
                              disabled={isInputDisabled}
                              style={{
                                width: '32px',
                                height: '32px',
                                borderRadius: '6px',
                                border: 'none',
                                background: btn.active ? '#475569' : 'transparent',
                                cursor: !isInputDisabled ? 'pointer' : 'not-allowed',
                                color: btn.active ? '#ffffff' : '#475569',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                transition: 'all 0.2s'
                              }}
                              onMouseOver={(e) => {
                                if (!isInputDisabled && !btn.active) {
                                  e.currentTarget.style.background = '#f1f5f9';
                                }
                              }}
                              onMouseOut={(e) => {
                                if (!btn.active) {
                                  e.currentTarget.style.background = 'transparent';
                                }
                              }}
                              title={btn.label}
                            >
                              <IconComponent size={15} />
                            </button>
                          );
                        })}

                        {/* Color Picker inside top toolbar */}
                        <div style={{ position: 'relative' }}>
                          <button
                            type="button"
                            onClick={() => {
                              setActiveFormat('foreColor');
                              setColorPickerOpen(prev => !prev);
                            }}
                            disabled={isInputDisabled}
                            style={{
                              width: '32px',
                              height: '32px',
                              borderRadius: '6px',
                              border: 'none',
                              background: activeFormat === 'foreColor' ? '#475569' : 'transparent',
                              cursor: !isInputDisabled ? 'pointer' : 'not-allowed',
                              color: activeFormat === 'foreColor' ? '#ffffff' : '#475569',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              transition: 'all 0.2s'
                            }}
                            onMouseOver={(e) => {
                              if (!isInputDisabled && activeFormat !== 'foreColor') {
                                e.currentTarget.style.background = '#f1f5f9';
                              }
                            }}
                            onMouseOut={(e) => {
                              if (activeFormat !== 'foreColor') {
                                e.currentTarget.style.background = 'transparent';
                              }
                            }}
                            title="Text color"
                          >
                            <Palette size={15} color={currentColor} />
                          </button>
                          {colorPickerOpen && (
                            <div style={{ position: 'absolute', top: '36px', left: 0, zIndex: 40, background: 'white', border: '1px solid #e5e7eb', borderRadius: '8px', padding: '6px', boxShadow: '0 4px 12px rgba(15,23,42,0.08)', display: 'flex', gap: '6px', alignItems: 'center' }}>
                              {['#111827', '#2563eb', '#dc2626', '#16a34a', '#f59e0b'].map(col => (
                                <button
                                  key={col}
                                  onClick={() => applyColor(col)}
                                  style={{ width: '22px', height: '22px', borderRadius: '4px', border: '1px solid #e2e8f0', background: col, cursor: 'pointer' }}
                                  title={col}
                                />
                              ))}
                              <input
                                type="color"
                                value={currentColor}
                                onChange={(e) => applyColor(e.target.value)}
                                style={{ width: '24px', height: '22px', padding: 0, border: 'none', background: 'transparent', cursor: 'pointer' }}
                              />
                            </div>
                          )}
                        </div>

                        {/* Clear Formatting inside top toolbar */}
                        <button
                          type="button"
                          onClick={clearFormatting}
                          disabled={isInputDisabled}
                          style={{
                            width: '32px',
                            height: '32px',
                            borderRadius: '6px',
                            border: 'none',
                            background: 'transparent',
                            cursor: !isInputDisabled ? 'pointer' : 'not-allowed',
                            color: '#475569',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            transition: 'all 0.2s'
                          }}
                          onMouseOver={(e) => {
                            if (!isInputDisabled) {
                              e.currentTarget.style.background = '#f1f5f9';
                            }
                          }}
                          onMouseOut={(e) => {
                            e.currentTarget.style.background = 'transparent';
                          }}
                          title="Clear formatting"
                        >
                          <Eraser size={15} />
                        </button>
                      </div>

                      {/* Middle Text Editor */}
                      <div style={{ position: 'relative', minHeight: '50px', display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
                        {isResolved ? (
                          <div style={{
                            width: '100%',
                            padding: '12px',
                            fontSize: '14px',
                            color: '#94a3b8',
                            background: '#f8fafc',
                            fontStyle: 'italic',
                            textAlign: 'center',
                            fontWeight: '700',
                            letterSpacing: '0.05em',
                            textTransform: 'uppercase'
                          }}>
                            Resolved
                          </div>
                        ) : (
                          <>
                            <div
                              ref={editorRef}
                              className="rich-text-editor"
                              contentEditable={!isInputDisabled}
                              suppressContentEditableWarning={true}
                              onInput={syncEditorState}
                              onKeyDown={handleKeyPress}
                              onPaste={handlePaste}
                              onKeyUp={updateFormattingState}
                              onMouseUp={updateFormattingState}
                              style={{
                                width: '100%',
                                padding: '6px 12px',
                                fontSize: '14px',
                                outline: 'none',
                                background: 'transparent',
                                overflowY: 'auto',
                                whiteSpace: 'pre-wrap',
                                wordBreak: 'break-word',
                                cursor: !isInputDisabled ? 'text' : 'not-allowed',
                                fontFamily: 'inherit',
                                color: '#1e293b'
                              }}
                            />
                            {!editorHtml && (
                              <div style={{
                                position: 'absolute',
                                left: '12px',
                                top: '6px',
                                pointerEvents: 'none',
                                color: isTakenOverByOther ? '#dc2626' : '#94a3b8',
                                fontSize: '14px',
                                fontWeight: isTakenOverByOther ? '600' : 'normal'
                              }}>
                                {inputPlaceholder}
                              </div>
                            )}
                          </>
                        )}
                      </div>

                      {attachedFile && (
                        <div style={{
                          margin: '0 14px 12px',
                          padding: '8px 12px',
                          background: '#f1f5f9',
                          borderRadius: '8px',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'space-between',
                          border: '1px solid #e2e8f0'
                        }}>
                          <div style={{ display: 'flex', alignItems: 'center', gap: '8px', overflow: 'hidden' }}>
                            <Paperclip size={14} color="#64748b" />
                            <span style={{ fontSize: '12px', color: '#334155', fontWeight: '500', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                              {attachedFile.name}
                            </span>
                          </div>
                          <button
                            type="button"
                            onClick={() => setAttachedFile(null)}
                            style={{
                              background: 'transparent',
                              border: 'none',
                              cursor: 'pointer',
                              color: '#94a3b8',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              padding: '4px',
                              borderRadius: '4px'
                            }}
                            onMouseOver={(e) => { e.currentTarget.style.color = '#ef4444'; e.currentTarget.style.background = '#e2e8f0'; }}
                            onMouseOut={(e) => { e.currentTarget.style.color = '#94a3b8'; e.currentTarget.style.background = 'transparent'; }}
                            title="Remove file"
                          >
                            <X size={14} />
                          </button>
                        </div>
                      )}


                      {/* Bottom Action Bar */}
                      <div style={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        padding: '4px 12px',
                        background: '#ffffff',
                        borderTop: '1px solid #f1f5f9',
                      }}>
                        {/* Left Actions: Attachments and Toggle */}
                        <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                          <button
                            type="button"
                            disabled={isResolved}
                            onClick={() => {
                              if (isResolved) return;
                              const input = document.createElement('input');
                              input.type = 'file';
                              input.accept = '*/*';
                              input.onchange = (e) => {
                                const file = e.target.files[0];
                                if (file) {
                                  setAttachedFile(file);
                                  showToast(`File attached: ${file.name}`);
                                }
                              };
                              input.click();
                            }}
                            style={{
                              width: '32px',
                              height: '32px',
                              borderRadius: '6px',
                              background: 'transparent',
                              border: 'none',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              color: '#475569',
                              cursor: isResolved ? 'not-allowed' : 'pointer',
                              opacity: isResolved ? 0.5 : 1
                            }}
                            onMouseOver={(e) => { if (!isResolved) e.currentTarget.style.background = '#f1f5f9'; }}
                            onMouseOut={(e) => e.currentTarget.style.background = 'transparent'}
                            title={isResolved ? 'Session resolved' : 'Attach file'}
                          >
                            <Paperclip size={15} />
                          </button>

                          {/* Emoji Picker Wrapper */}
                          <div style={{ position: 'relative' }}>
                            <button
                              type="button"
                              onClick={() => {
                                if (!isResolved && sessionId && (isHandoffAccepted || privateNoteMode)) {
                                  setEmojiPickerOpen(prev => !prev);
                                }
                              }}
                              disabled={isResolved || !sessionId || (!isHandoffAccepted && !privateNoteMode)}
                              style={{
                                width: '32px',
                                height: '32px',
                                borderRadius: '6px',
                                background: emojiPickerOpen ? '#475569' : 'transparent',
                                border: 'none',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                color: emojiPickerOpen ? '#ffffff' : '#475569',
                                cursor: (!isResolved && sessionId && (isHandoffAccepted || privateNoteMode)) ? 'pointer' : 'not-allowed',
                                opacity: isResolved ? 0.5 : 1
                              }}
                              onMouseOver={(e) => {
                                if (!isResolved && sessionId && (isHandoffAccepted || privateNoteMode) && !emojiPickerOpen) {
                                  e.currentTarget.style.background = '#f1f5f9';
                                }
                              }}
                              onMouseOut={(e) => {
                                if (!emojiPickerOpen) {
                                  e.currentTarget.style.background = 'transparent';
                                }
                              }}
                              title={isResolved ? 'Session resolved' : 'Add emoji'}
                            >
                              <Smile size={15} />
                            </button>
                            {emojiPickerOpen && (
                              <div style={{
                                position: 'absolute',
                                bottom: '38px',
                                left: 0,
                                zIndex: 40
                              }}>
                                <EmojiPicker
                                  onEmojiClick={(emojiData) => {
                                    const emoji = emojiData.emoji;
                                    if (editorRef.current) {
                                      editorRef.current.focus();
                                      document.execCommand('insertText', false, emoji);
                                      syncEditorState();
                                    }
                                    setEmojiPickerOpen(false);
                                  }}
                                  height={320}
                                  width={280}
                                  previewConfig={{ showPreview: false }}
                                />
                              </div>
                            )}
                          </div>

                          {/* Small Vertical Divider */}
                          <div style={{ width: '1px', height: '16px', background: '#e2e8f0', margin: '0 4px' }} />

                          {/* Pill Badge Mode Toggle */}
                          <button
                            type="button"
                            onClick={() => {
                              if (sessionId && !isResolved) {
                                setPrivateNoteMode(prev => !prev);
                              }
                            }}
                            disabled={!sessionId || isResolved}
                            style={{
                              display: 'flex',
                              alignItems: 'center',
                              gap: '6px',
                              padding: '5px 10px',
                              borderRadius: '16px',
                              fontSize: '11px',
                              fontWeight: '600',
                              cursor: (sessionId && !isResolved) ? 'pointer' : 'not-allowed',
                              border: '1px solid',
                              transition: 'all 0.2s',
                              background: isResolved ? '#f1f5f9' : (privateNoteMode ? '#fef3c7' : '#eff6ff'),
                              color: isResolved ? '#94a3b8' : (privateNoteMode ? '#b45309' : '#1d4ed8'),
                              borderColor: isResolved ? '#e2e8f0' : (privateNoteMode ? '#fde68a' : '#bfdbfe'),
                              opacity: isResolved ? 0.6 : 1
                            }}
                            title={isResolved ? 'Session resolved' : (privateNoteMode ? "Switch to public reply" : "Switch to private note")}
                          >
                            {privateNoteMode ? (
                              <>
                                <Lock size={12} style={{ marginRight: '2px' }} />
                                <span>Private Note</span>
                              </>
                            ) : (
                              <>
                                <MessageSquare size={12} style={{ marginRight: '2px' }} />
                                <span>Public Reply</span>
                              </>
                            )}
                          </button>
                        </div>

                        {/* Right Actions: Tag and Send */}
                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                          <button
                            onClick={handleAddTag}
                            disabled={!sessionId || isResolved}
                            style={{
                              display: 'flex',
                              alignItems: 'center',
                              gap: '4px',
                              padding: '5px 10px',
                              borderRadius: '6px',
                              background: '#ffffff',
                              border: '1px solid #e2e8f0',
                              color: (sessionId && !isResolved) ? '#475569' : '#94a3b8',
                              cursor: (sessionId && !isResolved) ? 'pointer' : 'not-allowed',
                              fontSize: '11px',
                              fontWeight: '600',
                              transition: 'all 0.2s',
                              opacity: isResolved ? 0.6 : 1
                            }}
                            onMouseOver={(e) => {
                              if (sessionId && !isResolved) {
                                e.currentTarget.style.background = '#f8fafc';
                              }
                            }}
                            onMouseOut={(e) => {
                              e.currentTarget.style.background = '#ffffff';
                            }}
                          >
                            <Tag size={13} />
                            <span>Add Tag</span>
                          </button>

                          <button
                            onClick={sendMessage}
                            disabled={isResolved || !(inputMessage.trim() && (privateNoteMode || isHandoffAccepted))}
                            style={{
                              display: 'flex',
                              alignItems: 'center',
                              gap: '6px',
                              padding: '6px 14px',
                              borderRadius: '6px',
                              background: (!isResolved && inputMessage.trim() && (privateNoteMode || isHandoffAccepted)) ? '#2563eb' : '#cbd5e1',
                              color: 'white',
                              border: 'none',
                              cursor: (!isResolved && inputMessage.trim() && (privateNoteMode || isHandoffAccepted)) ? 'pointer' : 'not-allowed',
                              fontSize: '12px',
                              fontWeight: '600',
                              transition: 'all 0.2s',
                              boxShadow: (!isResolved && inputMessage.trim() && (privateNoteMode || isHandoffAccepted)) ? '0 1px 2px rgba(37, 99, 235, 0.2)' : 'none'
                            }}
                            onMouseOver={(e) => {
                              if (!isResolved && inputMessage.trim() && (privateNoteMode || isHandoffAccepted)) {
                                e.currentTarget.style.background = '#1d4ed8';
                              }
                            }}
                            onMouseOut={(e) => {
                              if (!isResolved && inputMessage.trim() && (privateNoteMode || isHandoffAccepted)) {
                                e.currentTarget.style.background = '#2563eb';
                              }
                            }}
                          >
                            <Send size={13} />
                            <span>Send</span>
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              );
            })()}
          </div>
        </div>

        {/* RIGHT - Customer Intelligence */}
        <CustomerIntelligencePanel session={currentSession} messages={messages} tags={combinedTags} />
      </div>

      <DeleteConfirmModal
        isOpen={isDeleteModalOpen}
        onClose={() => setIsDeleteModalOpen(false)}
        onConfirm={confirmDeleteSession}
        isLoading={isDeletingSession}
        title="Delete this Chat?"
        message="This action will permanently terminate this conversation history. Proceed?"
      />

      <style>{`
        @keyframes slideDown {
          from { opacity: 0; transform: translateY(-20px); }
          to { opacity: 1; transform: translateY(0); }
        }
        .custom-scrollbar::-webkit-scrollbar {
          width: 8px;
        }
        .custom-scrollbar::-webkit-scrollbar-track {
          background: transparent;
        }
        .custom-scrollbar::-webkit-scrollbar-thumb {
          background: #d1d5db;
          border-radius: 4px;
        }
        .custom-scrollbar::-webkit-scrollbar-thumb:hover {
          background: #9ca3af;
        }
      `}</style>
    </div>
  );
};

export default HumanAgentChatInterface;
